import { useDeferredValue, useEffect, useMemo, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { readRecentVisits, searchableDestinations } from "../app/navigationHistory";
import { useBackend } from "../backend/BackendProvider";
import type { SearchHit } from "../domain/queries";
import { Dialog } from "./Dialog";

type CommandResult =
  | { key: string; label: string; description: string; path: string }
  | { key: string; label: string; description: string; hit: SearchHit };

export function SearchCommandDialog({ open, onClose, returnFocusRef }: { open: boolean; onClose: () => void; returnFocusRef: RefObject<HTMLElement | null> }) {
  const backend = useBackend();
  const navigate = useNavigate();
  const [value, setValue] = useState("");
  const [selected, setSelected] = useState(0);
  const text = useDeferredValue(value.trim());
  const recent = useMemo(() => readRecentVisits()
    .map((path) => searchableDestinations.find((item) => item.path === path))
    .filter((item): item is (typeof searchableDestinations)[number] => Boolean(item)), [open]);
  const query = useQuery({
    queryKey: ["command-search", text],
    queryFn: () => backend.search.search({ text }),
    enabled: open && Boolean(text),
  });
  const results = useMemo<CommandResult[]>(() => {
    if (!text) {
      const destinations = recent.length > 0 ? recent : searchableDestinations.slice(0, 6);
      return destinations.map((item) => ({ key: `destination-${item.path}`, label: item.label, description: item.description, path: item.path }));
    }
    const needle = text.toLocaleLowerCase("en-US");
    const destinations = searchableDestinations
      .filter((item) => `${item.label} ${item.aliases}`.toLocaleLowerCase("en-US").includes(needle))
      .map((item) => ({ key: `destination-${item.path}`, label: item.label, description: item.description, path: item.path }));
    const content = (query.data?.items ?? []).map((hit) => ({ key: `${hit.type}-${hit.id}`, label: hit.title, description: hit.subtitle ?? typeLabel(hit.type), hit }));
    return [...destinations, ...content];
  }, [query.data?.items, recent, text]);

  useEffect(() => { setSelected(0); }, [text]);
  useEffect(() => { if (!open) { setValue(""); setSelected(0); } }, [open]);

  const openResult = (result: CommandResult) => {
    onClose();
    navigate("path" in result ? result.path : targetFor(result.hit));
  };
  const openFullSearch = () => { onClose(); navigate("/search"); };

  return <Dialog open={open} title="Search" onClose={onClose} variant="command" returnFocusRef={returnFocusRef} footer={<><span className="dialog-hint">↑↓ Select · Enter Open · Esc Close</span><button className="button small" onClick={openFullSearch}>Open Full Search<ArrowRight size={14} /></button></>}>
    <div className="command-search-box"><Search size={18} /><input data-dialog-initial-focus aria-label="Command Search" aria-controls="command-search-results" aria-activedescendant={results[selected] ? `command-${results[selected].key}` : undefined} value={value} placeholder="Search Tasks and navigate…" onChange={(event) => setValue(event.target.value)} onKeyDown={(event) => {
      if (event.key === "ArrowDown") { event.preventDefault(); setSelected((index) => Math.max(0, Math.min(results.length - 1, index + 1))); }
      else if (event.key === "ArrowUp") { event.preventDefault(); setSelected((index) => Math.max(0, index - 1)); }
      else if (event.key === "Enter" && results[selected]) { event.preventDefault(); openResult(results[selected]); }
      else if (event.key === "Escape" && value) { event.preventDefault(); event.stopPropagation(); setValue(""); }
    }} />{value && <button className="icon-button compact" aria-label="Clear Command Search" onClick={() => setValue("")}><X size={15} /></button>}</div>
    <p className="command-search-label">{text ? "Results" : recent.length > 0 ? "Recently Visited" : "Common Destinations"}</p>
    {text && query.isLoading ? <div className="command-search-state" role="status">Searching…</div> : text && query.isError ? <div className="command-search-state error" role="alert"><span>Search failed. Your keywords were kept.</span><button className="button small" onClick={() => void query.refetch()}>Retry</button></div> : results.length > 0 ? <div id="command-search-results" className="command-search-results" role="listbox">{results.map((result, index) => <button id={`command-${result.key}`} role="option" aria-selected={selected === index} className={selected === index ? "selected" : ""} key={result.key} onMouseEnter={() => setSelected(index)} onClick={() => openResult(result)}><span><strong>{result.label}</strong><small>{result.description}</small></span><ArrowRight size={15} /></button>)}</div> : <div className="command-search-state">No matches. Open Full Search for filters or to create a Task.</div>}
  </Dialog>;
}

function targetFor(hit: SearchHit) {
  const targetId = hit.targetId ?? hit.id;
  if (hit.type === "project" || hit.type === "heading") return `/projects/${targetId}`;
  if (hit.type === "area") return `/areas/${targetId}`;
  if (hit.type === "tag") return `/tags/${targetId}`;
  return `/today?task=${encodeURIComponent(targetId)}`;
}

function typeLabel(type: SearchHit["type"]) {
  return ({ task: "Task", project: "Project", area: "Area", heading: "Heading", tag: "Tag" } as const)[type];
}
